/**
 * Shared utilities for player elements.
 */
import React from 'react';
import {
  Video, FileQuestion, BookOpen, Layers, GripVertical,
  ListChecks, CircleDot, Image, Headphones, Download, PenLine,
  Clock, MapPin, ArrowUpDown, GitBranch, ShieldCheck, MessageCircle,
} from 'lucide-react';

// YouTube IFrame API loader (singleton)
let ytApiPromise = null;
export const loadYTApi = () => {
  if (window.YT?.Player) return Promise.resolve();
  if (ytApiPromise) return ytApiPromise;
  ytApiPromise = new Promise((resolve) => {
    const existing = document.querySelector('script[src*="youtube.com/iframe_api"]');
    if (existing) {
      const check = setInterval(() => {
        if (window.YT?.Player) { clearInterval(check); resolve(); }
      }, 100);
      return;
    }
    window.onYouTubeIframeAPIReady = () => resolve();
    const tag = document.createElement('script');
    tag.src = 'https://www.youtube.com/iframe_api';
    document.head.appendChild(tag);
  });
  return ytApiPromise;
};

// Learning Style Mapping
const LEARNING_STYLES = {
  visual:      { label: 'Visual',           color: '#20acf6', types: ['video', 'image_hotspot', 'case_study'] },
  auditory:    { label: 'Auditory',         color: '#02d1d4', types: ['audio'] },
  reading:     { label: 'Reading/Writing',  color: '#2a3e5d', types: ['text', 'reflection_journal', 'downloadable', 'trainer_tip', 'decision_tree'] },
  kinesthetic: { label: 'Kinesthetic',      color: '#10B981', types: ['quiz', 'matching', 'fill_gaps', 'drag_drop_task', 'flashcard', 'timeline', 'label_diagram', 'sort_order', 'progress_checkpoint'] },
};

export const getLearningStyle = (elementType) => {
  for (const [key, style] of Object.entries(LEARNING_STYLES)) {
    if (style.types.includes(elementType)) return { key, ...style };
  }
  return { key: 'reading', ...LEARNING_STYLES.reading };
};

export const LearningStyleBadge = ({ type }) => {
  const style = getLearningStyle(type);
  const icons = { visual: '👁️', auditory: '👂', reading: '📖', kinesthetic: '✋' };
  return (
    <span
      className="inline-flex items-center gap-1 text-[10px] font-medium px-1.5 py-0.5 rounded-full"
      style={{ backgroundColor: `${style.color}15`, color: style.color }}
      data-testid={`learning-badge-${style.key}`}
    >
      {icons[style.key]} {style.label}
    </span>
  );
};

export const ElementIcon = ({ type, ...props }) => {
  const icons = {
    video: Video, quiz: FileQuestion, case_study: BookOpen,
    flashcard: Layers, fill_gaps: GripVertical, drag_drop_task: ListChecks,
    matching: CircleDot, image_hotspot: Image, audio: Headphones,
    downloadable: Download, reflection_journal: PenLine, text: BookOpen,
    trainer_tip: MessageCircle, timeline: Clock, label_diagram: MapPin,
    sort_order: ArrowUpDown, decision_tree: GitBranch, progress_checkpoint: ShieldCheck,
  };
  const Icon = icons[type] || BookOpen;
  return <Icon {...props} />;
};
